/**
 * Self-service permissions viewer + refresher for staff users.
 * https://github.com/gobranch/branch/issues/31758
 *
 * Staff users previously had no way to see their own Cognito permission groups (the
 * staff dashboard group editor requires CanViewTools + toolsViewEditUserGroups), and
 * permission changes only took effect after logging out and back in (groups are baked
 * into the ID token at issuance).
 *
 * Refresh is two-step (see auth.jsx):
 *  1. checkForPermissionChanges() force-refreshes the Cognito tokens and returns the
 *     added/removed diff without touching React state.
 *  2. applyRefreshedPermissions() rebuilds the live session so all useSession consumers
 *     pick up the new groups.
 * Additions-only diffs apply immediately. Diffs with removals require an explicit
 * confirmation first, because ProtectedRoute hard-redirects (window.location.href = '/')
 * the moment a permission it guards flips false — applying silently could navigate the
 * user off the page they're working on.
 */
import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import gql from 'graphql-tag';
import { useQuery } from '@apollo/client';
import Chip from '@material-ui/core/Chip';

import { Banner } from 'core/components/banner';
import BaseDialog from 'core/components/base-dialog';
import { Button } from 'core/components/button';
import { Label } from 'core/components/label';
import useSession from 'core/hooks/use-session';
import useStyles from './permissions-modal.styles';

// toolsGetLiveGroups is open to all authenticated staff users (group restriction
// removed alongside this feature) so everyone sees descriptions, not just names.
const GET_LIVE_GROUPS = gql`
  query getLiveGroups {
    groups: toolsGetLiveGroups {
      name
      description
    }
  }
`;

const REFRESH_STATUS = {
  IDLE: 'idle',
  CHECKING: 'checking',
  PENDING_REMOVALS: 'pendingRemovals',
  APPLYING: 'applying',
  APPLIED: 'applied',
  NO_CHANGES: 'noChanges',
  ERROR: 'error'
};

const PermissionsModal = ({ open, onClose }) => {
  const classes = useStyles();
  const { user, checkForPermissionChanges, applyRefreshedPermissions } = useSession();
  const [refreshStatus, setRefreshStatus] = useState(REFRESH_STATUS.IDLE);
  // Diff awaiting explicit confirmation because it contains removals
  const [pendingDiff, setPendingDiff] = useState(null);
  // Last applied diff — used to badge newly added groups in the list
  const [appliedDiff, setAppliedDiff] = useState(null);

  const { data: liveGroupsData } = useQuery(GET_LIVE_GROUPS, { skip: !open });

  const groupDescriptions = useMemo(
    () => Object.fromEntries((liveGroupsData?.groups || []).map((group) => [group.name, group.description])),
    [liveGroupsData]
  );

  const sortedGroups = useMemo(() => [...(user?.groups || [])].sort((a, b) => a.localeCompare(b)), [user]);

  const refreshing = refreshStatus === REFRESH_STATUS.CHECKING || refreshStatus === REFRESH_STATUS.APPLYING;

  const handleRefresh = async () => {
    setRefreshStatus(REFRESH_STATUS.CHECKING);
    setAppliedDiff(null);
    try {
      const diff = await checkForPermissionChanges();
      if (diff.removed.length > 0) {
        setPendingDiff(diff);
        setRefreshStatus(REFRESH_STATUS.PENDING_REMOVALS);
        return;
      }
      await applyRefreshedPermissions();
      setAppliedDiff(diff);
      setRefreshStatus(diff.added.length > 0 ? REFRESH_STATUS.APPLIED : REFRESH_STATUS.NO_CHANGES);
    } catch {
      setRefreshStatus(REFRESH_STATUS.ERROR);
    }
  };

  const handleApplyRemovals = async () => {
    setRefreshStatus(REFRESH_STATUS.APPLYING);
    try {
      await applyRefreshedPermissions();
      setAppliedDiff(pendingDiff);
      setPendingDiff(null);
      setRefreshStatus(REFRESH_STATUS.APPLIED);
    } catch {
      setRefreshStatus(REFRESH_STATUS.ERROR);
    }
  };

  const handleSkipRemovals = () => {
    // The tokens were already force-refreshed (API calls already use the new
    // permissions); skipping only defers the in-app UI flip to the next login/reload.
    setPendingDiff(null);
    setRefreshStatus(REFRESH_STATUS.IDLE);
  };

  const handleClose = () => {
    // Reset transient refresh state so a dismissed warning/banner doesn't reappear
    // stale the next time the modal opens (closing via backdrop bypasses "Not now").
    setRefreshStatus(REFRESH_STATUS.IDLE);
    setPendingDiff(null);
    setAppliedDiff(null);
    onClose();
  };

  return (
    <BaseDialog open={open} onClose={handleClose} size="md">
      <Label type="greenBig" className={classes.title}>
        Your permissions
      </Label>
      <Label type="darkGreenSmall" className={classes.subtitle}>
        If someone changed your permissions, refresh to apply them here — no need to log out and back in.
      </Label>

      {refreshStatus === REFRESH_STATUS.PENDING_REMOVALS && pendingDiff && (
        <div className={classes.bannerWrapper}>
          <Banner color="orange" show iconType="alert">
            <Label type="smallWhite" className={classes.bannerText}>
              {pendingDiff.removed.length} permission{pendingDiff.removed.length === 1 ? ' was' : 's were'} removed:{' '}
              {pendingDiff.removed.join(', ')}
              {pendingDiff.added.length > 0 && (
                <>
                  {' '}
                  (and {pendingDiff.added.length} added: {pendingDiff.added.join(', ')})
                </>
              )}
              . Applying may navigate you away from your current page if you no longer have access to it.
            </Label>
          </Banner>
        </div>
      )}

      {refreshStatus === REFRESH_STATUS.APPLIED && appliedDiff && (
        <div className={classes.bannerWrapper}>
          <Banner color="greenLight" show iconType="check">
            <Label type="smallWhite" className={classes.bannerText}>
              Permissions updated
              {appliedDiff.added.length > 0 && <> — added: {appliedDiff.added.join(', ')}</>}
              {appliedDiff.removed.length > 0 && <> — removed: {appliedDiff.removed.join(', ')}</>}. For a fully fresh
              start you can also{' '}
              <button type="button" className={classes.reloadLink} onClick={() => window.location.reload()}>
                reload the page
              </button>
              .
            </Label>
          </Banner>
        </div>
      )}

      {refreshStatus === REFRESH_STATUS.NO_CHANGES && (
        <div className={classes.bannerWrapper}>
          <Banner color="greenLight" show iconType="check">
            <Label type="smallWhite" className={classes.bannerText}>
              You&apos;re up to date — no permission changes found.
            </Label>
          </Banner>
        </div>
      )}

      {refreshStatus === REFRESH_STATUS.ERROR && (
        <div className={classes.bannerWrapper}>
          <Banner color="red" show iconType="alert">
            <Label type="smallWhite" className={classes.bannerText}>
              Couldn&apos;t refresh permissions. Try again, or log out and back in.
            </Label>
          </Banner>
        </div>
      )}

      {/* Counter mirrors the "TOTAL X items" pattern from FormSectionWrapper */}
      <div className={classes.totalRow}>
        <span className={classes.totalText}>TOTAL</span>
        <span className={classes.totalCount}>
          {sortedGroups.length} permission group{sortedGroups.length === 1 ? '' : 's'}
        </span>
      </div>

      <div className={classes.groupList}>
        {sortedGroups.map((group) => (
          <div key={group} className={classes.groupRow}>
            <Label type="body2Bold">{group}</Label>
            {appliedDiff?.added?.includes(group) && (
              <Chip label="New" size="small" color="primary" className={classes.newChip} />
            )}
            {groupDescriptions[group] && (
              <Label type="body3" className={classes.groupDescription}>
                {groupDescriptions[group]}
              </Label>
            )}
          </div>
        ))}
        {sortedGroups.length === 0 && (
          <div className={classes.groupRow}>
            <Label type="body2">No permission groups on your account.</Label>
          </div>
        )}
      </div>

      <div className={classes.footer}>
        {refreshStatus === REFRESH_STATUS.PENDING_REMOVALS ? (
          <>
            <Button variant="text" onClick={handleSkipRemovals}>
              Not now
            </Button>
            <Button variant="contained" color="secondary" onClick={handleApplyRemovals}>
              Apply changes
            </Button>
          </>
        ) : (
          <Button
            variant="contained"
            color="secondary"
            onClick={handleRefresh}
            loading={refreshing}
            disabled={refreshing}
          >
            Refresh permissions
          </Button>
        )}
      </div>
    </BaseDialog>
  );
};

PermissionsModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired
};

export default PermissionsModal;
